// WebCrypto and plain JS only: every helper here must run unchanged in a
// Worker without `nodejs_compat`, in Convex's default runtime (no
// `node:crypto`) and in the browser bundle.

/** A lowercase hex SHA-256 digest: exactly 64 characters. */
export const SHA256_HEX_REGEX = /^[0-9a-f]{64}$/;

/** Bytes or text; text is always hashed or keyed as its UTF-8 encoding. */
export type ByteInput = string | Uint8Array;

const encoder = new TextEncoder();

// `Uint8Array<ArrayBuffer>` rather than the bare alias: the bare one widens to
// `ArrayBufferLike`, which is not a `BufferSource`, so `crypto.subtle` would
// refuse it at the type level.
function toBytes(input: ByteInput): Uint8Array<ArrayBuffer> {
  return typeof input === "string"
    ? encoder.encode(input)
    : (input as Uint8Array<ArrayBuffer>);
}

export function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (const byte of bytes) hex += byte.toString(16).padStart(2, "0");
  return hex;
}

/** Unpadded base64url (RFC 4648 §5), the encoding of every JWT segment. */
export function bytesToBase64Url(bytes: Uint8Array): string {
  // One char at a time rather than `String.fromCharCode(...bytes)`, whose
  // spread overflows the argument stack on inputs of a few hundred KB.
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Decode unpadded base64url, or `null` for anything outside that alphabet.
 * Strict on purpose: a `+`, `/` or `=` means standard base64, which no
 * conforming signer of a base64url value produced.
 */
export function base64UrlToBytes(
  value: string,
): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(value)) return null;
  try {
    const binary = atob(value.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

export async function sha256(
  data: ByteInput,
): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", toBytes(data)));
}

/** Lowercase hex SHA-256. */
export async function sha256Hex(data: ByteInput): Promise<string> {
  return bytesToHex(await sha256(data));
}

export async function sha256Base64Url(data: ByteInput): Promise<string> {
  return bytesToBase64Url(await sha256(data));
}

export function importHmacSha256Key(
  key: ByteInput,
  usages: readonly ("sign" | "verify")[] = ["sign"],
): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    toBytes(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    [...usages],
  );
}

export async function hmacSha256(
  key: ByteInput,
  message: ByteInput,
): Promise<Uint8Array<ArrayBuffer>> {
  const cryptoKey = await importHmacSha256Key(key);
  return new Uint8Array(
    await crypto.subtle.sign("HMAC", cryptoKey, toBytes(message)),
  );
}

/** Lowercase hex HMAC-SHA256. */
export async function hmacSha256Hex(
  key: ByteInput,
  message: ByteInput,
): Promise<string> {
  return bytesToHex(await hmacSha256(key, message));
}

export async function hmacSha256Base64Url(
  key: ByteInput,
  message: ByteInput,
): Promise<string> {
  return bytesToBase64Url(await hmacSha256(key, message));
}

/**
 * Constant-time string compare, in pure JS because neither Convex's default
 * runtime nor a Worker without `nodejs_compat` has `timingSafeEqual`. The loop
 * walks the longer input so its length leaks nothing about which was shorter,
 * and the length difference is folded in so a prefix never compares equal.
 */
export function constantTimeEqual(a: string, b: string): boolean {
  const len = Math.max(a.length, b.length);
  let diff = a.length ^ b.length;
  for (let i = 0; i < len; i++) {
    // Past the end, compare against 0 so a length difference still sets bits.
    const ca = i < a.length ? a.charCodeAt(i) : 0;
    const cb = i < b.length ? b.charCodeAt(i) : 0;
    diff |= ca ^ cb;
  }
  return diff === 0;
}
